import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import {
  Save as SaveIcon,
  PlayArrow as RunIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Assessment as MonitoringIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import { MonacoJsonEditor } from "../../components/MonacoJsonEditor";
import { EmailAutomationPipelineResultsList } from "./EmailAutomationPipelineResultsList";
import { PipelineStep, ALL_STEPS, PIPELINE_STEPS } from "./constants";
import { VariableSyntaxHelp } from "./components/VariableSyntaxHelp";
import { RunPipelineDialog } from "./components/RunPipelineDialog";
import { DbObjectIdComponent } from "../../internal/components/DbObjectIdComponent";

// New format: flat array with category filters
interface PropertyDefinitionWithCategories {
  name: string;
  prompt: string;
  categories: string[];
}

interface PropertyExtractionStepConfig {
  properties: PropertyDefinitionWithCategories[];
}

interface ApiCallDefinition {
  name: string;
  categories: string[];
}

interface ApiFetchStepConfig {
  apiCalls: ApiCallDefinition[];
}

interface PresentationStepConfig {
  promptTemplate: string;
  categories?: string[];
}

type InboxEntry = string | { userEmail: string; sharedInbox?: string };

interface PipelineConfig {
  id: string;
  name: string;
  description: string | null;
  categorizationStepConfig: {
    promptStart?: string;
    promptEnd?: string;
    categories: Record<string, { prompt: string; matches?: Array<{ sender?: string; subject?: string; body?: string }> }>;
  };
  // Support both old and new formats (API returns migrated new format)
  propertyExtractionStepConfig: PropertyExtractionStepConfig;
  apiFetchStepConfig: ApiFetchStepConfig;
  presentationStepConfig: PresentationStepConfig;
  generateActionsStepConfig?: { prompt: string; availableActions: string[] } | null;
  evalStepConfig?: { prompt: string; structuredOutput: Record<string, string> };
  inboxes?: InboxEntry[] | null;
  createdAt: string;
  updatedAt: string;
}

interface TestSet {
  id: string;
  name: string;
  emailCount: number;
  shipmentCount: number;
  reminderCount: number;
  inputCount: number;
}

interface ActionDefinitionMetadata {
  name: string;
  description: string;
  fields: { name: string; description: string; type: string; required: boolean }[];
}

interface ApiCallReturnField {
  name: string;
  description: string;
  type: string;
}

interface ApiCallMetadata {
  name: string;
  description: string;
  returnFields: ApiCallReturnField[];
  configDescription?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export function EmailAutomationPipelineConfigPage() {
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [config, setConfig] = useState<PipelineConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Editable fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryDefinitionsJson, setCategoryDefinitionsJson] = useState("");
  const [propertyDefinitionsJson, setPropertyDefinitionsJson] = useState("");
  const [apiCallsJson, setApiCallsJson] = useState("");
  const [presentationPromptTemplate, setPresentationPromptTemplate] = useState("");
  const [generateActionsPrompt, setGenerateActionsPrompt] = useState("");
  const [generateActionsSelectedActions, setGenerateActionsSelectedActions] = useState<string[]>([]);
  const [evalPrompt, setEvalPrompt] = useState("");
  const [evalStructuredOutputJson, setEvalStructuredOutputJson] = useState("{}");

  // Inboxes (JSON editor)
  const [inboxesJson, setInboxesJson] = useState("[]");

  // Run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [selectedTestSetId, setSelectedTestSetId] = useState<string>("");
  const [emailIds, setEmailIds] = useState("");
  const [stepsToRun, setStepsToRun] = useState<PipelineStep[]>(ALL_STEPS);
  const [running, setRunning] = useState(false);
  const [runProgress, setRunProgress] = useState<{
    resultId: string;
    workflowId: string;
    processedCount: number;
    totalCount: number;
    status: string;
  } | null>(null);
  const [resultsRefreshTrigger, setResultsRefreshTrigger] = useState(0);
  const [inputMode, setInputMode] = useState<"emailIds" | "jsonInputs">("emailIds");
  const [inputsJson, setInputsJson] = useState("");

  // Available API calls
  const [availableApiCalls, setAvailableApiCalls] = useState<ApiCallMetadata[]>([]);
  // Available actions
  const [availableActions, setAvailableActions] = useState<ActionDefinitionMetadata[]>([]);

  // File input ref for importing config
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Export config as JSON file
  const handleExportConfig = () => {
    const exportData = {
      name,
      description: description || null,
      categorizationStepConfig: JSON.parse(categoryDefinitionsJson || "{}"),
      propertyExtractionStepConfig: JSON.parse(propertyDefinitionsJson || "{}"),
      apiFetchStepConfig: JSON.parse(apiCallsJson || "{}"),
      presentationStepConfig: { promptTemplate: presentationPromptTemplate },
      generateActionsStepConfig: generateActionsPrompt.trim()
        ? { prompt: generateActionsPrompt, availableActions: generateActionsSelectedActions }
        : null,
      evalStepConfig: evalPrompt.trim()
        ? { prompt: evalPrompt, structuredOutput: JSON.parse(evalStructuredOutputJson || "{}") }
        : null,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name.replace(/[^a-z0-9]/gi, "_")}_config.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSnackbar("Configuration exported", 2000, "success");
  };

  // Import config from JSON file
  const handleImportConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importedConfig = JSON.parse(content);

        // Apply imported values to form state
        if (importedConfig.name) setName(importedConfig.name);
        if (importedConfig.description !== undefined) {
          setDescription(importedConfig.description || "");
        }
        if (importedConfig.categorizationStepConfig) {
          setCategoryDefinitionsJson(
            JSON.stringify(importedConfig.categorizationStepConfig, null, 2)
          );
        }
        if (importedConfig.propertyExtractionStepConfig) {
          setPropertyDefinitionsJson(
            JSON.stringify(importedConfig.propertyExtractionStepConfig, null, 2)
          );
        }
        if (importedConfig.apiFetchStepConfig) {
          setApiCallsJson(
            JSON.stringify(importedConfig.apiFetchStepConfig, null, 2)
          );
        }
        if (importedConfig.presentationStepConfig?.promptTemplate !== undefined) {
          setPresentationPromptTemplate(
            importedConfig.presentationStepConfig.promptTemplate || ""
          );
        }
        if (importedConfig.generateActionsStepConfig) {
          setGenerateActionsPrompt(importedConfig.generateActionsStepConfig.prompt || "");
          setGenerateActionsSelectedActions(importedConfig.generateActionsStepConfig.availableActions || []);
        } else if (importedConfig.generateActionsStepConfig === null) {
          setGenerateActionsPrompt("");
          setGenerateActionsSelectedActions([]);
        }
        if (importedConfig.evalStepConfig) {
          setEvalPrompt(importedConfig.evalStepConfig.prompt || "");
          setEvalStructuredOutputJson(
            JSON.stringify(importedConfig.evalStepConfig.structuredOutput || {}, null, 2)
          );
        } else if (importedConfig.evalStepConfig === null) {
          setEvalPrompt("");
          setEvalStructuredOutputJson("{}");
        }

        showSnackbar(
          "Configuration loaded from file. Click Save to apply changes.",
          4000,
          "success"
        );
      } catch (err) {
        console.error("Error parsing config file:", err);
        showSnackbar("Invalid JSON file", 3000, "error");
      }
    };
    reader.readAsText(file);

    // Reset input so same file can be selected again
    event.target.value = "";
  };

  useEffect(() => {
    if (configId) {
      loadConfig();
    }
    loadAvailableApiCalls();
    loadAvailableActions();
  }, [configId]);

  const loadAvailableApiCalls = async () => {
    try {
      const res = await apiClient.get<{ apiCalls: ApiCallMetadata[] }>(
        `/admin/email-automation/api-calls/list`
      );
      setAvailableApiCalls(res.apiCalls);
    } catch (error) {
      console.error("Error loading available API calls:", error);
    }
  };

  const loadAvailableActions = async () => {
    try {
      const res = await apiClient.get<{ actions: ActionDefinitionMetadata[] }>(
        `/admin/email-automation/actions/list`
      );
      setAvailableActions(res.actions);
    } catch (error) {
      console.error("Error loading available actions:", error);
    }
  };

  // Poll for progress when running
  useEffect(() => {
    if (!runProgress || runProgress.status === "completed" || runProgress.status === "failed") {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const res = await apiClient.post<{ result: any }>(
          `/admin/email-automation/results/get`,
          { id: runProgress.resultId }
        );
        const result = res.result;

        setRunProgress(prev => prev ? {
          ...prev,
          processedCount: result.processedCount,
          totalCount: result.totalCount,
          status: result.workflowStatus,
        } : null);

        if (result.workflowStatus === "completed" || result.workflowStatus === "failed") {
          clearInterval(pollInterval);
          if (result.workflowStatus === "completed") {
            showSnackbar(`Pipeline completed: ${result.processedCount} email(s) processed`, 4000, "success");
            // Refresh results list
            setResultsRefreshTrigger((prev) => prev + 1);
            // Navigate to results page
            navigate(`/admin/email-automation/result/${runProgress.resultId}`);
          } else {
            showSnackbar(`Pipeline failed: ${result.errorMessage || 'Unknown error'}`, 4000, "error");
          }
          setRunning(false);
          setRunDialogOpen(false);
          setRunProgress(null);
        }
      } catch (error) {
        console.error("Error polling progress:", error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [runProgress, navigate, showSnackbar]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ config: PipelineConfig }>(
        `/admin/email-automation/config/get`,
        { id: configId }
      );
      setConfig(res.config);
      setName(res.config.name);
      setDescription(res.config.description || "");
      setCategoryDefinitionsJson(
        JSON.stringify(res.config.categorizationStepConfig, null, 2)
      );
      setPropertyDefinitionsJson(
        JSON.stringify(res.config.propertyExtractionStepConfig, null, 2)
      );
      setApiCallsJson(
        JSON.stringify(res.config.apiFetchStepConfig, null, 2)
      );
      setPresentationPromptTemplate(res.config.presentationStepConfig.promptTemplate || "");
      setGenerateActionsPrompt(res.config.generateActionsStepConfig?.prompt || "");
      setGenerateActionsSelectedActions(res.config.generateActionsStepConfig?.availableActions || []);
      setEvalPrompt(res.config.evalStepConfig?.prompt || "");
      setEvalStructuredOutputJson(
        JSON.stringify(res.config.evalStepConfig?.structuredOutput || {}, null, 2)
      );
      setInboxesJson(JSON.stringify(res.config.inboxes || [], null, 2));
    } catch (error) {
      console.error("Error loading config:", error);
      setError("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!configId) return;

    // Validate JSON fields
    let categoryDefinitions, propertyDefinitions, apiCalls, evalStructuredOutput;
    try {
      categoryDefinitions = JSON.parse(categoryDefinitionsJson);
    } catch {
      setError("Invalid JSON in Category Definitions");
      setTabValue(1);
      return;
    }
    try {
      propertyDefinitions = JSON.parse(propertyDefinitionsJson);
    } catch {
      setError("Invalid JSON in Property Extraction Definitions");
      setTabValue(2);
      return;
    }
    try {
      apiCalls = JSON.parse(apiCallsJson);
    } catch {
      setError("Invalid JSON in API Calls Configuration");
      setTabValue(3);
      return;
    }
    try {
      evalStructuredOutput = JSON.parse(evalStructuredOutputJson);
    } catch {
      setError("Invalid JSON in Evaluation Structured Output");
      setTabValue(6);
      return;
    }
    let parsedInboxes: any[];
    try {
      parsedInboxes = JSON.parse(inboxesJson);
      if (!Array.isArray(parsedInboxes)) throw new Error("Must be an array");
    } catch {
      setError("Invalid JSON in Inboxes");
      setTabValue(0);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`/admin/email-automation/config/update`, {
        id: configId,
        name: name.trim(),
        description: description.trim() || undefined,
        categorizationStepConfig: categoryDefinitions,
        propertyExtractionStepConfig: propertyDefinitions,
        apiFetchStepConfig: apiCalls,
        presentationStepConfig: { promptTemplate: presentationPromptTemplate },
        generateActionsStepConfig: generateActionsPrompt.trim()
          ? { prompt: generateActionsPrompt, availableActions: generateActionsSelectedActions }
          : null,
        evalStepConfig: evalPrompt.trim()
          ? { prompt: evalPrompt, structuredOutput: evalStructuredOutput }
          : null,
        inboxes: parsedInboxes.length > 0 ? parsedInboxes : null,
      });
      showSnackbar("Configuration saved successfully", 3000, "success");
      loadConfig();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to save configuration";
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const loadTestSets = async () => {
    try {
      const res = await apiClient.post<{ testSets: TestSet[] }>(
        `/admin/email-test-sets/list`,
        {}
      );
      setTestSets(res.testSets);
    } catch (error) {
      console.error("Error loading test sets:", error);
    }
  };

  const handleOpenRunDialog = () => {
    setRunDialogOpen(true);
    loadTestSets();
    setSelectedTestSetId("");
    setEmailIds("");
    setStepsToRun(ALL_STEPS);
    setRunProgress(null);
    setInputMode("emailIds");
    setInputsJson("");
  };

  const handleRunPipeline = async () => {
    if (!configId) return;

    if (inputMode === "jsonInputs") {
      let parsedInputs: any[];
      try {
        parsedInputs = JSON.parse(inputsJson);
        if (!Array.isArray(parsedInputs) || parsedInputs.length === 0) {
          showSnackbar("JSON must be a non-empty array of inputs", 3000, "error");
          return;
        }
      } catch {
        showSnackbar("Invalid JSON", 3000, "error");
        return;
      }

      setRunning(true);
      try {
        const res = await apiClient.post<{ resultId: string; workflowId: string }>(
          `/admin/email-automation/run`,
          {
            configId,
            inputs: parsedInputs,
            stepsToRun: stepsToRun.length === ALL_STEPS.length ? undefined : stepsToRun,
          }
        );

        setRunProgress({
          resultId: res.resultId,
          workflowId: res.workflowId,
          processedCount: 0,
          totalCount: parsedInputs.length,
          status: "pending",
        });

        showSnackbar("Pipeline started! Processing inputs...", 3000, "info");
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.error || "Failed to start pipeline";
        showSnackbar(errorMessage, 4000, "error");
        setRunning(false);
      }
      return;
    }

    const emailIdList = emailIds
      .split(/[,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (emailIdList.length === 0 && !selectedTestSetId) {
      showSnackbar(
        "Please enter email IDs or select a test set",
        3000,
        "info"
      );
      return;
    }

    setRunning(true);
    try {
      const res = await apiClient.post<{ resultId: string; workflowId: string }>(
        `/admin/email-automation/run`,
        {
          configId,
          inputs: emailIdList.length > 0 ? emailIdList.map(id => ({ emailId: id })) : undefined,
          testSetId: selectedTestSetId || undefined,
          stepsToRun: stepsToRun.length === ALL_STEPS.length ? undefined : stepsToRun,
        }
      );

      // Set initial progress state to start polling
      setRunProgress({
        resultId: res.resultId,
        workflowId: res.workflowId,
        processedCount: 0,
        totalCount: emailIdList.length || 0,
        status: "pending",
      });

      showSnackbar("Pipeline started! Processing emails...", 3000, "info");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to start pipeline";
      showSnackbar(errorMessage, 4000, "error");
      setRunning(false);
    }
  };

  const handleStepToggle = (step: PipelineStep) => {
    setStepsToRun((prev) =>
      prev.includes(step) ? prev.filter((s) => s !== step) : [...prev, step]
    );
  };

  if (loading) {
    return (
      <AdminPage title="Edit Configuration">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (!config) {
    return (
      <AdminPage title="Edit Configuration">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Configuration not found</Alert>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
            sx={{ mt: 2 }}
          >
            Back to List
          </Button>
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage title={`Edit: ${config.name}`}>
      <Box sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-automation")}
          >
            Back to List
          </Button>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MonitoringIcon />}
              onClick={() => navigate(`/admin/email-automation/monitoring/${configId}`)}
            >
              Monitoring
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportConfig}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Import
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImportConfig}
              accept=".json"
              style={{ display: "none" }}
            />
            <Button
              variant="outlined"
              startIcon={<RunIcon />}
              onClick={handleOpenRunDialog}
            >
              Run Pipeline
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Basic Info" />
            <Tab label="Categories" />
            <Tab label="Property Extraction" />
            <Tab label="API Calls" />
            <Tab label="Presentation" />
            <Tab label="Generate Actions" />
            <Tab label="Evaluation" />
          </Tabs>

          {/* Basic Info Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Config ID:
                  </Typography>
                  {configId && (
                    <DbObjectIdComponent
                      value={configId}
                      table="emailAutomationPipelineConfig"
                      label="Config"
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>

              {/* Inboxes Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" sx={{ mb: 1, mt: 1 }}>
                  Inboxes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  JSON array of inboxes to monitor. Each entry is either an email string{" "}
                  (<code>"user@example.com"</code>) or an object for shared inboxes{" "}
                  (<code>{`{ "userEmail": "user@example.com", "sharedInbox": "shared@example.com" }`}</code>).
                </Typography>
                <MonacoJsonEditor
                  value={inboxesJson}
                  onChange={setInboxesJson}
                  height={150}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* Categories Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define email categories. Each key is a category name, with a
              "prompt" field describing when to apply the category.
            </Typography>
            <MonacoJsonEditor
              value={categoryDefinitionsJson}
              onChange={setCategoryDefinitionsJson}
              height={500}
            />
          </TabPanel>

          {/* Property Extraction Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define properties to extract. Each property has a name, extraction prompt,
              and a list of categories it applies to (must have at least one matching category).
              Access via <code>$propertyExtraction.&lt;name&gt;</code> in templates.
            </Typography>
            <MonacoJsonEditor
              value={propertyDefinitionsJson}
              onChange={setPropertyDefinitionsJson}
              height={500}
            />
          </TabPanel>

          {/* API Calls Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure which API calls to execute. Each API call has a name and
              list of categories it applies to (must have at least one matching category).
              Access via <code>$apiFetch.&lt;apiCallName&gt;.&lt;field&gt;</code> in templates.
            </Typography>

            {/* Available API Calls */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Available API Calls
              </Typography>
              {availableApiCalls.length > 0 ? (
                <Box>
                  {availableApiCalls.map((apiCall) => (
                    <Accordion
                      key={apiCall.name}
                      sx={{
                        "&:before": { display: "none" },
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:not(:last-child)": { borderBottom: 0 },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                          <Typography
                            sx={{ fontFamily: "monospace", fontWeight: 500 }}
                          >
                            {apiCall.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ flex: 1 }}
                          >
                            {apiCall.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {/* Config Description */}
                        {apiCall.configDescription && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                              Configuration
                            </Typography>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                bgcolor: "info.lighter",
                                border: "1px solid",
                                borderColor: "info.light",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                                {apiCall.configDescription}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                          Return Fields
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: "30%" }}>Field</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: "15%" }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {apiCall.returnFields.map((field) => (
                              <TableRow key={field.name}>
                                <TableCell sx={{ fontFamily: "monospace" }}>
                                  {field.name}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      bgcolor: "action.hover",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {field.type}
                                  </Typography>
                                </TableCell>
                                <TableCell>{field.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No API calls registered yet.
                </Typography>
              )}
            </Box>

            <MonacoJsonEditor
              value={apiCallsJson}
              onChange={setApiCallsJson}
              height={450}
            />
          </TabPanel>

          {/* Presentation Tab */}
          <TabPanel value={tabValue} index={4}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define the presentation prompt template. The output will be structured as sections
              with titles and markdown content for display in Outlook.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Structured Output Format
              </Typography>
              <Typography variant="body2" component="div">
                The presentation step generates structured sections:
                <Box
                  component="pre"
                  sx={{
                    margin: "8px 0",
                    fontSize: "0.8rem",
                    backgroundColor: "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                  }}
                >
{`{
  "sections": [
    { "title": "Summary", "content": "**Key points**\\n- Point 1\\n- Point 2" },
    { "title": "Action Items", "content": "1. First action\\n2. Second action" },
    {
      "title": "Related Emails",
      "content": "Emails matching parcel identifiers:",
      "emails": [
        { "id": "...", "subject": "RE: Shipment", "from": "...", "to": [...], "date": "...", "preview": "...", "attachmentCount": 2 }
      ]
    }
  ]
}`}
                </Box>
                Each section's content supports Markdown formatting. The optional{" "}
                <code>emails</code> field displays clickable email references from API fetch results
                (e.g., <code>$apiFetch.alice_track_parcel.relatedEmails</code>).
              </Typography>
            </Alert>
            <VariableSyntaxHelp />
            <TextField
              fullWidth
              multiline
              rows={20}
              value={presentationPromptTemplate}
              onChange={(e) => setPresentationPromptTemplate(e.target.value)}
              sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
              placeholder="Generate a summary using $categorization.categories and $propertyExtraction.etd..."
            />
          </TabPanel>

          {/* Generate Actions Tab */}
          <TabPanel value={tabValue} index={5}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Configure the generate actions step. This step uses shipment context to produce
              actionable outputs (e.g., draft emails). It runs for shipment-based inputs only.
            </Typography>
            <VariableSyntaxHelp />

            {/* Available Actions Selection */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Available Actions
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Select which actions the LLM can generate. The action definitions will be
              automatically injected into the prompt.
            </Typography>
            {availableActions.length > 0 ? (
              <FormGroup sx={{ mb: 3 }}>
                {availableActions.map((action) => (
                  <Box key={action.name}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={generateActionsSelectedActions.includes(action.name)}
                          onChange={(e) => {
                            setGenerateActionsSelectedActions((prev) =>
                              e.target.checked
                                ? [...prev, action.name]
                                : prev.filter((a) => a !== action.name)
                            );
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontFamily: "monospace", fontWeight: 500 }}>
                            {action.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {action.description}
                          </Typography>
                        </Box>
                      }
                    />
                    {generateActionsSelectedActions.includes(action.name) && action.fields.length > 0 && (
                      <Box sx={{ ml: 4, mb: 1 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: "25%" }}>Field</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: "15%" }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: "15%" }}>Required</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {action.fields.map((field) => (
                              <TableRow key={field.name}>
                                <TableCell sx={{ fontFamily: "monospace" }}>{field.name}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="caption"
                                    sx={{ px: 1, py: 0.25, borderRadius: 1, bgcolor: "action.hover", fontFamily: "monospace" }}
                                  >
                                    {field.type}
                                  </Typography>
                                </TableCell>
                                <TableCell>{field.required ? "Yes" : "No"}</TableCell>
                                <TableCell>{field.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Box>
                ))}
              </FormGroup>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No actions registered yet.
              </Typography>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Generate Actions Prompt
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={generateActionsPrompt}
              onChange={(e) => setGenerateActionsPrompt(e.target.value)}
              sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
              placeholder="Based on $shipmentContext, generate actions to handle this shipment..."
            />
          </TabPanel>

          {/* Evaluation Tab */}
          <TabPanel value={tabValue} index={6}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Define an evaluation prompt to assess pipeline results.
              Use structured variable syntax to inject data from previous steps.
            </Typography>
            <VariableSyntaxHelp />
            <Typography variant="subtitle2" gutterBottom>
              Evaluation Prompt
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={12}
              value={evalPrompt}
              onChange={(e) => setEvalPrompt(e.target.value)}
              sx={{ fontFamily: "monospace", fontSize: "0.875rem", mb: 3 }}
              placeholder="Evaluate the quality of $presentation.result based on $categorization.categories..."
            />
            <Typography variant="subtitle2" gutterBottom>
              Structured Output Schema
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Define the output fields (keys will be strings in response).
            </Typography>
            <MonacoJsonEditor
              value={evalStructuredOutputJson}
              onChange={setEvalStructuredOutputJson}
              height={200}
            />
          </TabPanel>
        </Paper>

        {/* Results List */}
        <Box sx={{ mt: 3 }}>
          <EmailAutomationPipelineResultsList
            configId={configId}
            refreshTrigger={resultsRefreshTrigger}
          />
        </Box>

        {/* Run Pipeline Dialog */}
        <RunPipelineDialog
          open={runDialogOpen}
          onClose={() => setRunDialogOpen(false)}
          testSets={testSets}
          selectedTestSetId={selectedTestSetId}
          setSelectedTestSetId={setSelectedTestSetId}
          emailIds={emailIds}
          setEmailIds={setEmailIds}
          stepsToRun={stepsToRun}
          onStepToggle={handleStepToggle}
          running={running}
          runProgress={runProgress}
          onRunPipeline={handleRunPipeline}
          inputMode={inputMode}
          setInputMode={setInputMode}
          inputsJson={inputsJson}
          setInputsJson={setInputsJson}
        />
      </Box>
    </AdminPage>
  );
}
